/**
 * Usage badge (Data dock · "where used") — a compact "N usi" chip beside a query
 * or a page variable that expands to the list of consumers, each a click-to-jump
 * row. Powers the inspector (slice 3) AND the "inutilizzata" lint (slice 4).
 *
 * The lint is honest about the static-analysis limit (see `pageReferences.ts`):
 * `count === 0` shows an amber "inutilizzata" ONLY when the page has no opaque
 * consumer; with `hasOpaque` it softens to "0 usi*" (a custom_component / js query
 * could read it invisibly), so the author is never told "safe to delete" on a
 * guess.
 */
import { ArrowDownToLine, ArrowUpFromLine, ChevronRight } from "lucide-react";
import * as React from "react";

import type { Usage } from "@/lib/pageReferences";

export function UsageBadge({
  usages,
  hasOpaque,
  onJump,
}: {
  usages: Usage[];
  /** True when the page has a custom_component / js query the scan can't see. */
  hasOpaque: boolean;
  /** Jump to a consumer (select the node, or switch to the query). */
  onJump?: (u: Usage) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const count = usages.length;

  if (count === 0) {
    // Unused lint. Amber + assertive only when nothing opaque could be hiding a use.
    return (
      <span
        className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-medium ${
          hasOpaque
            ? "bg-muted/40 text-muted-foreground"
            : "bg-warning/15 text-warning"
        }`}
        title={
          hasOpaque
            ? "Nessun uso statico trovato, ma la pagina ha un componente custom o una query JS che potrebbe usarla."
            : "Nessun componente o query la usa."
        }
      >
        {hasOpaque ? "0 usi*" : "inutilizzata"}
      </span>
    );
  }

  return (
    <span className="relative shrink-0">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex items-center gap-0.5 rounded bg-accent/15 px-1.5 py-0.5 text-[9px] font-medium text-accent hover:bg-accent/25"
        title="Dove è usata"
      >
        <ChevronRight
          className={`h-2.5 w-2.5 transition-transform ${open ? "rotate-90" : ""}`}
          strokeWidth={2}
        />
        {count} {count === 1 ? "uso" : "usi"}
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-md border border-border bg-elevated shadow-lg">
          {usages.map((u, i) => (
            <button
              key={`${u.nodeId}:${u.field}:${i}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onJump?.(u);
              }}
              className="flex w-full items-center gap-1.5 border-b border-border/40 px-2 py-1.5 text-left text-[11px] last:border-b-0 hover:bg-surface"
            >
              {u.direction === "write" ? (
                <ArrowUpFromLine className="h-3 w-3 shrink-0 text-warning" strokeWidth={1.5} />
              ) : (
                <ArrowDownToLine className="h-3 w-3 shrink-0 text-info" strokeWidth={1.5} />
              )}
              <span className="min-w-0 flex-1 truncate">{u.label}</span>
              <span className="shrink-0 text-[9px] text-muted-foreground/70">{u.field}</span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
